import {Paper} from '@material-ui/core';
import {toEither} from 'Core/models/Async';
import {EmptyString, NonEmptyString} from 'Core/models/EmptyString';
import {sequenceT} from 'fp-ts/lib/Apply';
import {either, fold as foldEither} from 'fp-ts/lib/Either';
import {fold} from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {useEffect} from 'react';
import {connect} from 'react-redux';
import {ReducerState} from 'rootReducer';
import Loading from 'Shared/components/Loading';
import {getNameFromClaims} from 'Shared/components/nameFormatter';
import {renderAsync, renderAsyncView} from 'Shared/components/RenderAsync';
import withUserClaims, {WithClaimsProps} from 'Shared/components/withUserClaims';
import {getIsUserEmployee} from 'Shared/utilities/Auth';
import EmployeeUserInformationSection from 'Users/components/UserProfile/EmployeeUserInformationSection';
import UserEmailSection from 'Users/components/UserProfile/UserEmailSection';
import UserInformationSection from 'Users/components/UserProfile/UserInformationSection';
import UserMobilePhoneSection from 'Users/components/UserProfile/UserMobilePhoneSection';
import UserSecurityQuestionsSection from 'Users/components/UserProfile/UserSecurityQuestionsSection';
import * as UserAccountActions from 'Users/ducks/userAccount';
import {UnchangedNameInformation} from 'Users/models/NameInformation';
import PasswordResetSection from './PasswordResetSection';
import './UserProfile.sass';

type UserProfileProps = ReturnType<typeof mapStateToProps> & WithClaimsProps & typeof UserAccountActions;

export const LoadingTile = () => (
  <Paper className="user-profile__user-info-section">
    <Loading />
  </Paper>
);

export const UserProfile = (props: UserProfileProps) => {
  const {
    changeEmail,
    changeMobilePhone,
    changeNameInformation,
    changeSecurityQuestionForm,
    claims,
    currentNameInformation,
    email,
    getSecurityQuestions,
    getUserProfile,
    mobilePhone,
    nameInformation,
    profile,
    saveEmail,
    saveMobilePhone,
    saveNameInformation,
    saveSecurityQuestions,
    securityQuestions,
    securityQuestionForm,
    changeUserError,
  } = props;
  const {id} = claims;

  const defaultNameInformation = pipe(
    currentNameInformation,
    fold(
      () => new UnchangedNameInformation(new EmptyString(), new EmptyString()),
      names =>
        new UnchangedNameInformation(NonEmptyString.create(names.firstName), NonEmptyString.create(names.lastName))
    )
  );
  useEffect(() => {
    getUserProfile(id);
    getSecurityQuestions();
    return () => {
      changeUserError([]);
    };
  }, [id, getSecurityQuestions, getUserProfile]);

  const securityQuestionData = sequenceT(either)(toEither(securityQuestions), toEither(securityQuestionForm));

  const IAUserInformationSection = (
    <div>
      <UserInformationSection
        userId={id}
        saveNameInformation={saveNameInformation}
        currentNameInformation={defaultNameInformation}
        nameInformation={nameInformation}
        onChange={changeNameInformation}
        errors={props.errors}
        onChangeUserError={props.changeUserError}
      />
      {renderAsyncView(
        email,
        loadedEmail => {
          return <UserEmailSection onChange={changeEmail} userId={id} asyncEmail={loadedEmail} saveEmail={saveEmail} />;
        },
        LoadingTile
      )}
      {renderAsyncView(
        mobilePhone,
        loadedPhone => {
          return (
            <UserMobilePhoneSection
              onChange={changeMobilePhone}
              userId={id}
              phoneEither={loadedPhone}
              saveMobilePhone={saveMobilePhone}
            />
          );
        },
        LoadingTile
      )}
      {pipe(
        securityQuestionData,
        foldEither(
          error => renderAsync(error, LoadingTile),
          ([securityQuestions, securityQuestionForm]) => {
            return (
              <UserSecurityQuestionsSection
                onChange={changeSecurityQuestionForm}
                userId={id}
                saveSecurityQuestions={saveSecurityQuestions}
                securityQuestionForm={securityQuestionForm}
                securityQuestions={securityQuestions}
              />
            );
          }
        )
      )}
      <PasswordResetSection />
    </div>
  );

  return (
    <div className="user-profile">
      {pipe(
        getIsUserEmployee(),
        fold(
          () => IAUserInformationSection,
          isEmployee =>
            isEmployee
              ? renderAsyncView(profile, loadedProfile => (
                  <EmployeeUserInformationSection
                    profile={loadedProfile}
                    showPosition={loadedProfile.position !== 'None'}
                  />
                ))
              : IAUserInformationSection
        )
      )}
    </div>
  );
};

const mapStateToProps = (state: ReducerState) => ({
  nameInformation: state.user.userAccount.nameInformation,
  currentNameInformation: getNameFromClaims(state.user.profile.userClaims),
  profile: state.user.userAccount.userProfile,
  email: state.user.userAccount.email,
  mobilePhone: state.user.userAccount.mobilePhone,
  securityQuestions: state.user.userAccount.questions,
  securityQuestionForm: state.user.userAccount.securityQuestionForm,
  errors: state.user.userAccount.validationError,
});

export default withUserClaims(
  connect(mapStateToProps, {
    ...UserAccountActions,
  })(UserProfile)
);
